/// Port of mobile analytics ingest against `/v1/analytics/*`.

import { appEnv } from '@/app/env/appEnv';
import { apiClient } from '@/app/network/apiClient';
import {
  readOrCreateAnalyticsSessionId,
  readOrCreateAnonymousId,
  rotateAnalyticsSessionId,
} from '@/user/data/userLocalData';

function analyticsEnvironment(): 'dev' | 'qa' | 'prod' {
  switch (appEnv.environment) {
    case 'prod':
      return 'prod';
    case 'qa':
      return 'qa';
    default:
      return 'dev';
  }
}

function basePayload(): Record<string, unknown> {
  return {
    anonymous_id: readOrCreateAnonymousId(),
    platform: 'web',
    app_version: appEnv.version,
    app_build: 'web',
    environment: analyticsEnvironment(),
    session_id: readOrCreateAnalyticsSessionId(),
    timezone_offset_minutes: -new Date().getTimezoneOffset(),
    locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    device_model: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : undefined,
  };
}

let lastHeartbeatAt = 0;
const heartbeatMinMs = 10 * 60 * 1000;

export function startAnalyticsSession(): void {
  rotateAnalyticsSessionId();
  void heartbeat(true);
}

export async function heartbeat(force = false): Promise<void> {
  const now = Date.now();
  if (!force && now - lastHeartbeatAt < heartbeatMinMs) return;
  try {
    await apiClient.post('/v1/analytics/heartbeat', basePayload(), { anonymous: true });
    lastHeartbeatAt = now;
  } catch {
    // Best-effort.
  }
}

export async function submitGuestRun(input: {
  score: number;
  durationMs: number;
  jumpCount: number;
  duckCount: number;
  deathCause: string;
  wasPersonalBest: boolean;
}): Promise<void> {
  try {
    await apiClient.post(
      '/v1/analytics/runs',
      {
        ...basePayload(),
        score: input.score,
        duration_ms: input.durationMs,
        jump_count: input.jumpCount,
        duck_count: input.duckCount,
        death_cause: input.deathCause,
        was_personal_best: input.wasPersonalBest,
        platform: 'web',
      },
      { anonymous: true },
    );
  } catch {
    // Best-effort.
  }
}

export async function trackEvent(
  name: string,
  properties?: Record<string, unknown>,
): Promise<void> {
  try {
    await apiClient.post(
      '/v1/analytics/events',
      {
        ...basePayload(),
        name,
        properties: properties ?? {},
      },
      { anonymous: true },
    );
  } catch {
    // Best-effort.
  }
}

export function anonymousId(): string {
  return readOrCreateAnonymousId();
}

export function sessionId(): string {
  return readOrCreateAnalyticsSessionId();
}
